import {HoistModel, LoadSpec, SelectOption, TaskObserver, XH} from '@xh/hoist/core';
import {CytoGraphModel} from '../../../cmp/cytoGraph/CytoGraphModel';
import {action, bindable, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {CtxMenuArgs, EdgeCreatedArgs, IEdgeData, IGraphEdge, IGraphElement, IGraphNode, INodeData, NodeMoveArgs} from '../../../cmp/cytoGraph/CytoGraphTypes';
import {AccountStructure, AccountStructureItemType, ManagementTypeEnum, Portfolio} from '../../../data/DataTypes';
import {isEmpty, sortBy} from 'lodash';
import {getDefaultStyle, registerExpandCallback, typeToClass} from './NodeStyles';
import {fmtDateTimeSec} from '@xh/hoist/format';
import {wait} from '@xh/hoist/promise';

export interface NodeSelectOption extends SelectOption {
    type: any;
}

export class AccountStructureModel extends HoistModel {
    private accountStructure: AccountStructure;
    private prefKey = 'accountStructureTabState';
    apiTask = TaskObserver.trackLast();
    graphModelRef: CytoGraphModel;
    @observableRef accessor elements: IGraphElement[] = [];
    @observableRef accessor selectedItem: INodeData;
    @observable accessor showBackground: boolean;
    @bindable accessor searchNodeId: string;

    constructor() {
        super();

        registerExpandCallback(id => {
            this.graphModelRef.expandCollapse(id);
            this.elements.forEach(e => {
                const ge = this.graphModelRef.getNode(e.data.id);
                e.data.style = ge.data().style;
            });
            this.saveStateAsync();
        });

        this.addReaction({
            track: () => XH.portfolioService.accountStructure,
            run: () => this.loadAsync(),
            fireImmediately: true
        });

        this.addReaction({
            track: () => this.searchNodeId,
            run: id => {
                if (id) this.jumpToItem();
            }
        });

        this.showBackground = XH.getPref(this.prefKey)?.showBackground;
    }

    get searchItems(): NodeSelectOption[] {
        const nodes = this.elements.filter(e => this.isNode(e));
        const items = nodes.map(n => {
            return {label: n.data.customData.name, value: n.data.id, type: n.data.customData.type};
        });

        return sortBy(items, ['type', 'label']);
    }

    @action
    onNodeSelect(id: string) {
        if (isEmpty(id)) {
            this.selectedItem = null;
            return null;
        }

        const element = this.elements.find(e => e.data.id == id);
        this.selectedItem = element.data;
    }

    onNodeAction(id: string) {}

    onNodeMove(args: NodeMoveArgs) {
        const node = this.elements.find(e => e.data.id == args.id);
        if (!node) return;

        this.elements = [...this.elements.filter(e => e.data.id != args.id), {...node, position: args.position}];
        this.saveStateAsync();
    }

    @action
    createEdge(args: EdgeCreatedArgs): boolean {
        const isValid = this.validateEdge(args);
        if (!isValid) return false;

        wait().then(async () => {
            const newEdge: IGraphEdge = {data: args};
            runInAction(() => (this.elements = [...this.elements, newEdge]));

            await this.saveStateAsync();

            const targetNode = this.elements.find(e => e.data.id == args.target);
            if (targetNode.data.customData.type == AccountStructureItemType.PORTFOLIO) {
                XH.livePositionsService.loadAsync();
            }
        });

        return true;
    }

    private validateEdge(args: EdgeCreatedArgs): boolean {
        const sourceData = this.elements.find(e => e.data.id == args.source).data.customData,
            targetData = this.elements.find(e => e.data.id == args.target).data.customData;

        if (args.source == args.target) return false;
        if (sourceData.type == AccountStructureItemType.PORTFOLIO) return false;
        if (sourceData.type == AccountStructureItemType.BROKERAGE && targetData.type != AccountStructureItemType.PORTFOLIO) return false;
        if (sourceData.type == AccountStructureItemType.CUSTOM && targetData.type == AccountStructureItemType.PORTFOLIO) return false;

        return true;
    }

    @action
    async deleteEdge(args: CtxMenuArgs) {
        this.elements = [...this.elements.filter(e => e.data.id != args.targetId)];

        await this.saveStateAsync();
        XH.livePositionsService.loadAsync();
    }

    @action
    async deleteNode(args: CtxMenuArgs) {
        if (this.selectedItem.customData.type == AccountStructureItemType.PORTFOLIO) {
            const {portfolioId} = this.selectedItem.customData;
            const hasDependents = await XH.portfolioService.fetchPortfolioHasDependentsAsync(portfolioId);
            if (hasDependents) {
                await XH.alert({
                    message: 'Portfolio can not be deleted as it has transactions/positions'
                });
                return;
            } else {
                await XH.portfolioService.deletePortfolioAsync(portfolioId);
            }
        }

        // remove related edges as well
        this.elements = this.elements.filter(
            e => e.data.id != args.targetId && (<IEdgeData>e.data)?.source != args.targetId && (<IEdgeData>e.data)?.target != args.targetId
        );

        this.selectedItem = null;
        this.saveStateAsync();
    }

    @action
    async addBrokerageNode(args: CtxMenuArgs) {
        const name = 'New Brokerage',
            newNode: IGraphNode = {
                data: {
                    id: `b_${this.getId()}`,
                    customData: {type: AccountStructureItemType.BROKERAGE, name},
                    style: getDefaultStyle(AccountStructureItemType.BROKERAGE)
                },
                classes: 'node-brokerage',
                position: args.graphOffset
            };
        this.elements = [...this.elements, newNode];

        await this.saveStateAsync();

        this.graphModelRef.selectElement(newNode.data.id);
    }

    async addPortfolioNode(args: CtxMenuArgs) {
        const portfolio: Portfolio = {
            id: 0,
            userId: 0,
            portfolioName: 'New Portfolio',
            portfolioId: '',
            brokerage: ''
        };
        await XH.portfolioService.savePortfolioAsync(portfolio);
        await XH.portfolioService.refreshPortfoliosAsync();

        const newNode: IGraphNode = {
            data: {
                id: `p_${portfolio.id}`,
                customData: {
                    type: AccountStructureItemType.PORTFOLIO,
                    portfolioId: portfolio.id,
                    name: portfolio.portfolioName
                },
                style: getDefaultStyle(AccountStructureItemType.PORTFOLIO)
            },
            classes: 'node-portfolio',
            position: args.graphOffset
        };

        runInAction(() => (this.elements = [...this.elements, newNode]));

        await this.saveStateAsync();

        this.graphModelRef.selectElement(newNode.data.id);
    }

    async addCustomNode(args: CtxMenuArgs) {
        const name = 'Custom',
            newNode: IGraphNode = {
                data: {
                    id: `c_${this.getId()}`,
                    customData: {type: AccountStructureItemType.CUSTOM, name},
                    style: getDefaultStyle(AccountStructureItemType.CUSTOM)
                },
                classes: 'node-custom',
                position: args.graphOffset
            };
        this.elements = [...this.elements, newNode];

        await this.saveStateAsync();

        this.graphModelRef.selectElement(newNode.data.id);
    }

    resetLayout() {
        this.graphModelRef.resetLayout();

        for (const e of this.elements) {
            if (!this.isNode(e)) continue;
            const src = this.graphModelRef.getNode(e.data.id);
            e.position = src.position();
        }

        this.saveStateAsync();
    }

    resetView() {
        this.graphModelRef.resetView();
    }

    expandAll() {
        this.graphModelRef.expandAll();

        this.elements.forEach(e => {
            const ge = this.graphModelRef.getNode(e.data.id);
            e.data.style = ge.data().style;
        });
        this.saveStateAsync();
    }

    @action
    toggleBackground() {
        this.showBackground = !this.showBackground;
        XH.setPref(this.prefKey, {
            ...XH.getPref(this.prefKey),
            showBackground: this.showBackground
        });
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (loadSpec.isStale || loadSpec.isObsolete) return;

        loadSpec.loadNumber;
        const accountStructure = XH.portfolioService.accountStructure;

        accountStructure.items.forEach(item => {
            item.classes = typeToClass(item.data.customData?.type);
            if (!item.data.style) item.data.style = getDefaultStyle(item.data.customData?.type);

            if (item.data.customData?.type == AccountStructureItemType.PORTFOLIO) {
                const portfolio = XH.portfolioService.portfoliosById.get(item.data.customData.portfolioId);
                if (portfolio) {
                    item.data.customData.name = portfolio.portfolioName;
                    item.data.customData.tax = portfolio.taxableType;
                    item.data.customData.isManaged = portfolio.managementType == ManagementTypeEnum.Managed;
                    item.data.customData.timestamp = fmtDateTimeSec(portfolio.updated);
                    item.data.style.opacity = portfolio.isActive ? 1 : 0.4;
                }
            }
        });

        this.accountStructure = accountStructure;
        runInAction(() => (this.elements = this.accountStructure.items));
    }

    private isNode(e: IGraphElement) {
        return e.data['source'] == null;
    }

    private getId(): number {
        return new Date().getTime();
    }

    private async saveStateAsync() {
        const newStructure = {...this.accountStructure, items: this.elements};
        await XH.portfolioService.saveAccountStructureAsync(newStructure);
        await XH.portfolioService.reloadAsync();
    }

    private jumpToItem() {
        this.expandAll();
        this.graphModelRef.centerSelectNode(this.searchNodeId);

        setTimeout(() => runInAction(() => (this.searchNodeId = null)), 100);
    }
}
